import * as signalR from '@microsoft/signalr';
import angular from 'angular';
import ngModule from './ngmodule';
import { IAuthService, ITokenAuthService } from './auth/auth-svc';

ngModule.factory('vmSignalHub', ['$q', '$timeout', 'authSvc', function ($q: angular.IQService, $timeout: angular.ITimeoutService, authSvc: ITokenAuthService) {
    let connection: signalR.HubConnection | null = null;
    let isConnected: boolean = false;
    let startPromise: angular.IDeferred<void> | null = null;
    let virtualConnections: VirtualConnection[] = [];

    function createRealConnection(): void {
        connection = new signalR.HubConnectionBuilder()
            .withUrl('/signal/hub', {
                accessTokenFactory: () => {
                    return new Promise<string>((resolve, reject) => {
                        authSvc.getAccessToken()
                            .then(token => {
                                resolve(token as string);
                            })
                            .catch(err => {
                                reject(err);
                            });
                    });
                }
            })
            .withAutomaticReconnect()
            .build();

        // Handle connection events
        connection.onreconnected(() => {
            isConnected = true;
            console.log('SignalR Reconnected');

            // Re-register all event handlers
            virtualConnections.forEach(vc => vc._reconnected());
        });

        connection.onreconnecting(() => {
            isConnected = false;
            console.log('SignalR Reconnecting');
        });

        connection.onclose(() => {
            isConnected = false;
            console.log('SignalR Connection Closed');
        });
    }

    function startConnection(): angular.IPromise<void> {
        if (!connection) {
            createRealConnection();
        }
        if (!startPromise) {
            startPromise = $q.defer<void>();
            connection!.start()
                .then(() => {
                    isConnected = true;
                    console.log('SignalR Started and Connected');
                    startPromise!.resolve();
                })
                .catch((err: any) => {
                    isConnected = false;
                    console.error(`SignalR Connection Error: ${err}`);
                    startPromise!.reject(err);
                });
        }
        return startPromise.promise;
    }

    function stopConnection(): angular.IPromise<void> {
        if (connection && isConnected) {
            let stopPromise = $q.defer<void>();
            connection.stop()
                .then(() => {
                    isConnected = false;
                    startPromise = null;
                    connection = null;
                    console.log('SignalR Connection Stopped');
                    stopPromise.resolve();
                })
                .catch((err: any) => {
                    console.error(`Error stopping SignalR connection: ${err}`);
                    stopPromise.reject(err);
                });
            return stopPromise.promise;
        } else {
            return $q.resolve();
        }
    }

    // Expose a method to create virtual connections
    let vmSignalHubService = {
        createVirtualConnection: function (): VirtualConnection {
            let vc = new VirtualConnection();
            virtualConnections.push(vc);
            // Start the real connection if not already started
            startConnection().then(() => {
                vc._reconnected();
            });
            return vc;
        },
        _removeVirtualConnection: function (vc: VirtualConnection): void {
            let index = virtualConnections.indexOf(vc);
            if (index !== -1) {
                virtualConnections.splice(index, 1);
            }
            // Stop the real connection if no more virtual connections
            if (virtualConnections.length === 0) {
                stopConnection();
            }
        },
        isConnected: function (): boolean {
            return isConnected;
        }
    };

    class VirtualConnection {
        private offs: (() => void)[] = [];
        private isDisposed: boolean = false;
        private invokeQueue: (() => void)[] = [];
        private subscriptions: (() => void)[] = [];
        private unsubscriptions: (() => void)[] = [];
        private vcConnected: boolean = false;

        constructor() {
            // Return a Proxy to intercept method calls
            return new Proxy(this, {
                get: (target: any, propKey: string | symbol, receiver: any) => {
                    if (propKey === 'isConnected') {
                        return isConnected;
                    } else if (typeof propKey === 'string' && propKey in target) {
                        return (target as any)[propKey];
                    } else if (typeof propKey === 'string' && propKey.startsWith('on')) {
                        // Dynamically handle the 'on' method
                        return (callback: (...args: any[]) => void) => {
                            if (this.isDisposed) return;
                            let eventName = propKey.substring(2);

                            let wrappedCallback = (...args: any[]) => {
                                // Use $timeout to integrate with AngularJS digest cycle
                                $timeout(() => {
                                    callback.apply(null, args);
                                });
                            };
                            connection!.on(eventName, wrappedCallback);

                            let off = () => {
                                if (this.isDisposed) return;
                                connection!.off(eventName, wrappedCallback);
                            }
                            this.offs.push(off);

                            return off;
                        };
                    } else if (typeof propKey === 'string' && propKey.startsWith('subscribe')) {
                        // Dynamically handle methods starting with 'subscribe'
                        return (...args: any[]) => {
                            if (this.isDisposed) return $q.reject('Virtual connection is disposed.');
                            let subscribeMethod = propKey;
                            let unsubscribeMethod = 'unsubscribe' + subscribeMethod.substring(9);

                            let deferred = $q.defer();

                            let invokeUnsubscribe: (() => void) | null = null;
                            let invokeSubscribe = () => {
                                connection!.invoke(subscribeMethod, ...args)
                                    .then(deferred.resolve)
                                    .catch(deferred.reject);
                                invokeUnsubscribe = () => {
                                    connection!.invoke(unsubscribeMethod, ...args)
                                        .catch((err: any) => {
                                            console.error(`Error invoking ${unsubscribeMethod}: ${err}`);
                                        });
                                }
                                this.unsubscriptions.push(invokeUnsubscribe!);
                            };

                            this.subscriptions.push(invokeSubscribe);

                            // Note, we have to wait for VirtualConnection._reconnected to be called before invoking the subscribe
                            // to avoid duplicate subscriptions
                            if (isConnected && this.vcConnected) {
                                invokeSubscribe();
                            }

                            // Add 'unsubscribe' method to the deferred promise
                            (deferred.promise as any).unsubscribe = () => {
                                if (invokeUnsubscribe) {
                                    invokeUnsubscribe();
                                    this.unsubscriptions = this.unsubscriptions.filter(unsub => unsub !== invokeUnsubscribe);
                                }
                                invokeUnsubscribe = null;
                                this.subscriptions = this.subscriptions.filter(sub => sub !== invokeSubscribe);
                            }
                            return deferred.promise;
                        };
                    } else {
                        // Everything else is treated as a method call
                        return (...args: any[]) => {
                            if (this.isDisposed) return $q.reject('Virtual connection is disposed.');
                            let methodName = propKey.toString();

                            let deferred = $q.defer();

                            let invokeAction = () => {
                                connection!.invoke(methodName, ...args)
                                    .then(deferred.resolve)
                                    .catch(deferred.reject);
                            };

                            if (isConnected) {
                                invokeAction();
                            } else {
                                this.invokeQueue.push(invokeAction);
                            }
                            return deferred.promise;
                        };
                    }
                }
            });
        }

        dispose(): void {
            if (this.isDisposed) return;

            // Remove event handlers
            this.offs.forEach(off => off());
            this.offs = [];

            this.unsubscriptions.forEach(unsub => unsub());
            this.unsubscriptions = [];

            // Remove from vmSignalHub's virtual connections
            vmSignalHubService._removeVirtualConnection(this);

            this.isDisposed = true;
        }

        // Internal method called when the real connection is established or re-established
        _reconnected(): void {
            if (this.isDisposed) return;
            this.vcConnected = true;

            // Re-invoke subscriptions
            this.subscriptions.forEach(subscribe => subscribe());

            // Invoke any queued method calls
            this.invokeQueue.forEach(action => action());
            this.invokeQueue = [];
        }
    }

    return vmSignalHubService;
}]);
